import { useEffect, useRef, useState } from 'react'
import { Container, Col, Row, Form, Spinner, Button, Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import home_logo from '../../assets/icons/ICRI_NEW1.png';
import MockService from '../../Services/MockService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faRotateRight, faPencil, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

const LABEL = { fontSize: '0.7rem', fontWeight: 600, color: '#6C757D', letterSpacing: '0.05em', textTransform: 'uppercase' as const };
const SECTION_TITLE = { fontSize: '0.7rem', fontWeight: 700, color: '#3B00A8', letterSpacing: '0.08em', textTransform: 'uppercase' as const, marginBottom: '0.75rem' };

export default function Onboarding() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        name: '', last_name: '', gender: '', mobile: '', dob: '', city: '', image: '',
    });

    const [editing, setEditing] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [cameraReady, setCameraReady] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const fetchProfile = async () => {
        await MockService.getMockProfile()
            .then((res) => {
                if (res.status === 200) {
                    const user = res.data?.data?.user;
                    setProfile(user);
                    if (user) {
                        setForm({
                            name: user.name || user.first_name || '',
                            last_name: user.last_name || '',
                            gender: user.gender || '',
                            mobile: user.mobile || user.phone || '',
                            dob: user.dob ? new Date(user.dob).toISOString().split('T')[0] : '',
                            city: user.city || '',
                            image: user.image || user.photo || '',
                        });
                    }
                }
            })
            .catch((err) => console.error("Error fetching profile:", err))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchProfile(); }, []);

    const isComplete = !!(form.name && form.last_name && form.gender && form.mobile && form.dob && form.city && form.image);

    const onChange = (e: any) => setForm({ ...form, [e.target.name]: e.target.value });

    const openCamera = async () => {
        setShowCamera(true); setCameraReady(false);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = () => setCameraReady(true);
            }
        } catch { toast.error('Could not access camera'); setShowCamera(false); }
    };

    const stopCamera = () => {
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null; setCameraReady(false);
    };
    const closeCamera = () => { stopCamera(); setShowCamera(false); };

    const capture = () => {
        const video = videoRef.current; const canvas = canvasRef.current;
        if (!video || !canvas) return;
        canvas.width = video.videoWidth; canvas.height = video.videoHeight;
        canvas.getContext('2d')!.drawImage(video, 0, 0);
        setForm(prev => ({ ...prev, image: canvas.toDataURL('image/jpeg', 0.85) }));
        stopCamera(); setShowCamera(false); toast.success('Photo captured');
    };

    const handleSubmit = async () => {
        setSaving(true);
        await MockService.mockProfileUpdate({ ...form, dob: form.dob ? new Date(form.dob).toISOString() : '' })
            .then((res) => {
                if (res.status === 200) { toast.success('Profile updated'); navigate('/exam/student-info'); }
            })
            .catch(() => toast.error('Failed to update profile'))
            .finally(() => setSaving(false));
    };

    return (
        <div style={{ minHeight: '100vh', background: '#F4F5F8' }}>
            {/* Thin top accent */}
            <div style={{ height: 3, background: 'linear-gradient(90deg, #3B00A8, #7C3AED)' }} />

            <Container style={{ maxWidth: 600 }} className="py-4">
                {/* Header */}
                <div className="d-flex flex-column align-items-center mb-4">
                    <img src={home_logo} alt="logo" style={{ height: 52, marginBottom: 12 }} />
                    <h5 style={{ fontWeight: 700, color: '#1A1A2E', marginBottom: 4 }}>Complete Your Profile</h5>
                    <p style={{ color: '#6C757D', fontSize: '0.825rem', margin: 0 }}>Fill in your details to continue</p>
                </div>

                {/* Read-only identity card */}
                <div style={{ background: '#fff', borderRadius: '0.75rem', border: '1.5px solid #EDE9FF', padding: '1.25rem', marginBottom: '1rem' }}>
                    <p style={SECTION_TITLE}>Account Information</p>
                    {loading ? (
                        <div className="d-flex justify-content-center py-2">
                            <Spinner animation="border" size="sm" style={{ color: '#3B00A8' }} />
                        </div>
                    ) : (
                        <Row>
                            {[
                                { label: 'Email', value: profile?.email },
                                { label: 'Registration No.', value: profile?.registrationNumber },
                                { label: 'Center', value: profile?.center_name },
                                { label: 'Username', value: profile?.username },
                            ].map(({ label, value }) => (
                                <Col md={6} key={label}>
                                    <Form.Group className="mb-2">
                                        <Form.Label style={LABEL}>{label}</Form.Label>
                                        <Form.Control
                                            readOnly
                                            value={value || '\u2014'}
                                            size="sm"
                                            style={{ background: '#F8F9FA', border: '1.5px solid #EDE9FF', borderRadius: '0.375rem', color: '#1A1A2E' }}
                                        />
                                    </Form.Group>
                                </Col>
                            ))}
                        </Row>
                    )}
                </div>

                {/* Personal Details — info view when complete, form when editing/incomplete */}
                <div style={{ background: '#fff', borderRadius: '0.75rem', border: '1.5px solid #EDE9FF', padding: '1.5rem' }}>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <p style={{ ...SECTION_TITLE, marginBottom: 0 }}>Personal Details</p>
                        {isComplete && !editing && (
                            <button
                                onClick={() => setEditing(true)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: '1px solid #3B00A8', borderRadius: 6, padding: '3px 10px', color: '#3B00A8', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}
                            >
                                <FontAwesomeIcon icon={faPencil} style={{ fontSize: 10 }} /> Edit
                            </button>
                        )}
                    </div>

                    {isComplete && !editing ? (
                        /* ── Read-only info view ── */
                        <>
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <img src={form.image} alt="profile" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 10, border: '2px solid #EDE9FF' }} />
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1A1A2E' }}>{form.name} {form.last_name}</div>
                                    <div style={{ fontSize: '0.78rem', color: '#6C757D', marginTop: 2 }}>{form.city}</div>
                                </div>
                                <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 99, padding: '3px 10px', fontSize: '0.7rem', fontWeight: 600, color: '#16A34A' }}>
                                    <FontAwesomeIcon icon={faCheckCircle} /> Complete
                                </span>
                            </div>
                            <Row>
                                {[
                                    { label: 'Gender', value: form.gender },
                                    { label: 'Mobile', value: form.mobile },
                                    { label: 'Date of Birth', value: form.dob },
                                    { label: 'City', value: form.city },
                                ].map(({ label, value }) => (
                                    <Col md={6} key={label} className="mb-2">
                                        <div style={{ ...LABEL, marginBottom: 2 }}>{label}</div>
                                        <div style={{ fontSize: '0.85rem', color: '#1A1A2E', fontWeight: 500 }}>{value || '\u2014'}</div>
                                    </Col>
                                ))}
                            </Row>
                            <Button
                                variant="primary"
                                size="lg"
                                className="w-100 mt-3"
                                onClick={handleSubmit}
                                disabled={saving}
                                style={{ fontWeight: 600 }}
                            >
                                {saving ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                                {saving ? 'Saving\u2026' : 'Continue'}
                            </Button>
                        </>
                    ) : (
                        /* ── Editable form ── */
                        <>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={LABEL}>First Name</Form.Label>
                                        <Form.Control type="text" name="name" placeholder="First name" value={form.name} onChange={onChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={LABEL}>Last Name</Form.Label>
                                        <Form.Control type="text" name="last_name" placeholder="Last name" value={form.last_name} onChange={onChange} />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={LABEL}>Gender</Form.Label>
                                        <Form.Select name="gender" value={form.gender} onChange={onChange}>
                                            <option value="">Select gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={LABEL}>Mobile</Form.Label>
                                        <Form.Control type="text" name="mobile" placeholder="Mobile number" value={form.mobile} onChange={onChange} />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={LABEL}>Date of Birth</Form.Label>
                                        <Form.Control type="date" name="dob" value={form.dob} onChange={onChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label style={LABEL}>City</Form.Label>
                                        <Form.Control type="text" name="city" placeholder="City" value={form.city} onChange={onChange} />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-4">
                                <Form.Label style={LABEL}>Photo</Form.Label>
                                <div className="d-flex align-items-center gap-3">
                                    {form.image ? (
                                        <>
                                            <img src={form.image} alt="captured" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: '2px solid #EDE9FF' }} />
                                            <Button variant="outline-secondary" size="sm" onClick={openCamera}>
                                                <FontAwesomeIcon icon={faRotateRight} className="me-1" /> Retake
                                            </Button>
                                        </>
                                    ) : (
                                        <Button variant="outline-primary" size="sm" onClick={openCamera} style={{ borderStyle: 'dashed' }}>
                                            <FontAwesomeIcon icon={faCamera} className="me-2" /> Open Camera
                                        </Button>
                                    )}
                                </div>
                            </Form.Group>

                            <div className="d-flex gap-2">
                                {editing && (
                                    <Button variant="outline-secondary" size="lg" className="flex-shrink-0" onClick={() => setEditing(false)} style={{ fontWeight: 600 }}>
                                        Cancel
                                    </Button>
                                )}
                                <Button
                                    variant="primary"
                                    size="lg"
                                    className="w-100"
                                    onClick={handleSubmit}
                                    disabled={saving}
                                    style={{ fontWeight: 600 }}
                                >
                                    {saving ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                                    {saving ? 'Saving\u2026' : editing ? 'Save Changes' : 'Save & Continue'}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Container>

            {/* Camera Modal */}
            <Modal show={showCamera} onHide={closeCamera} centered>
                <Modal.Header closeButton style={{ borderBottom: '1.5px solid #EDE9FF' }}>
                    <Modal.Title style={{ fontSize: '0.95rem', fontWeight: 600 }}>Capture Photo</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0" style={{ background: '#000', borderRadius: '0 0 0.75rem 0.75rem' }}>
                    <video ref={videoRef} autoPlay playsInline style={{ width: '100%', display: 'block' }} />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1.5px solid #EDE9FF' }}>
                    <Button variant="outline-secondary" size="sm" onClick={closeCamera}>Cancel</Button>
                    <Button variant="primary" size="sm" onClick={capture} disabled={!cameraReady} style={{ fontWeight: 600 }}>
                        <FontAwesomeIcon icon={faCamera} className="me-1" /> Capture
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
